import React, {
	createContext, useCallback, useEffect, useMemo, useRef,
} from 'react';
import { useSelector } from 'react-redux';
import useShots from '../shots/provider';
import { GreenroomState } from '../../stores/types/types';
import { ShotFeed } from '../../stores/reducers/shots';

interface PreloadedInfo {
	videoIndices:(number|undefined)[],
	type:'media' |'audio'|'presenter' |'screenshare',
	position:number
}
interface VideoCacheContextType {
	preloadedElements: null | React.MutableRefObject<Map<string, Map<number, PreloadedInfo>>>;
}

export const VideoCacheContext = createContext<VideoCacheContextType>({
	preloadedElements: null,
});

interface Props {
	children: React.ReactNode;
}

export const UseVideoCache = (): VideoCacheContextType => {
	const shots = useSelector((state: GreenroomState) => state.ShotsReducer.shots);

	const { currentShot } = useShots();

	const preloadedElements = useRef(new Map<string, Map<number, PreloadedInfo>>());

	// the next shot in the show
	const nextShot = useMemo(() => {
		const nextShotOrder = currentShot.shot_data.order + 1;
		const nNextShot = shots.find((shot) => shot.shot_data.order === nextShotOrder);
		return nNextShot;
	}, [currentShot, shots]);

	// used to re-render the hook and attached new videos to the dom in a useEffect
	useEffect(() => {
		if (!currentShot) { return; }

		const newUrlsByMediaId = new Map<string, Map<number, PreloadedInfo>>();
		newUrlsByMediaId.set('current-shot', new Map());
		newUrlsByMediaId.set('next-shot', new Map());

		currentShot.shot_data.shotFeeds.forEach((feed: ShotFeed) => {
			const {
				media: mediaId, feedType, presenter, position, screenshare,
			} = feed;

			if ((feedType === 'media' || feedType === 'audio') && (mediaId || mediaId === 0)) {
				newUrlsByMediaId.get('current-shot')?.set(mediaId, { videoIndices: [1], type: feedType, position });
			} else if (feedType === 'presenter' && presenter) {
				newUrlsByMediaId.get('current-shot')?.set(presenter, { videoIndices: [1], type: feedType, position });
			} else if ((feedType === 'screenshare' && screenshare)) {
				newUrlsByMediaId.get('current-shot')?.set(screenshare as unknown as number, { videoIndices: [1], type: feedType, position });
			}
		});

		nextShot?.shot_data.shotFeeds.forEach((feed: ShotFeed) => {
			const {
				media: mediaId, feedType, presenter, position, screenshare,
			} = feed;
			if (feedType === 'media' && (mediaId || mediaId === 0)) {
				newUrlsByMediaId.get('next-shot')?.set(mediaId, { videoIndices: [1], type: feedType, position });
			} else if (feedType === 'presenter' && presenter) {
				newUrlsByMediaId.get('next-shot')?.set(presenter, { videoIndices: [1], type: feedType, position });
			} else if ((feedType === 'screenshare' && screenshare)) {
				newUrlsByMediaId.get('next-shot')?.set(screenshare as unknown as number, { videoIndices: [1], type: feedType, position });
			}
		});

		// whenever the shot changes, this will trigger
		// and update the preloadedElements map
		// newUrlsByMediaId.forEach(createAndAttachVideos);
		preloadedElements.current = newUrlsByMediaId;
	}, [currentShot.shot_data.shotFeeds, nextShot?.shot_data.shotFeeds]);

	return {
		preloadedElements,
	};
};

const VideoCacheProvider: React.FC<Props> = ({ children }: Props) => {
	const videoCacheContext = UseVideoCache();
	return <VideoCacheContext.Provider value={videoCacheContext}>
		{children}
	</VideoCacheContext.Provider>;
};

export default VideoCacheProvider;
